import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, isSameDay, addMonths, isWeekend, parseISO } from 'date-fns';
import { invoicingService } from '../../services/invoicingService';
import api from '../../services/api';

// UK Bank Holidays 2025-2027
const UK_BANK_HOLIDAYS = [
  // 2025
  '2025-01-01', // New Year's Day
  '2025-04-18', // Good Friday
  '2025-04-21', // Easter Monday
  '2025-05-05', // Early May Bank Holiday
  '2025-05-26', // Spring Bank Holiday
  '2025-08-25', // Summer Bank Holiday
  '2025-12-25', // Christmas Day
  '2025-12-26', // Boxing Day
  // 2026
  '2026-01-01', // New Year's Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-04', // Early May Bank Holiday
  '2026-05-25', // Spring Bank Holiday
  '2026-08-31', // Summer Bank Holiday
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (substitute - 26th is Saturday)
  // 2027
  '2027-01-01', // New Year's Day
  '2027-03-26', // Good Friday
  '2027-03-29', // Easter Monday
  '2027-05-03', // Early May Bank Holiday
  '2027-05-31', // Spring Bank Holiday
  '2027-08-30', // Summer Bank Holiday
  '2027-12-27', // Christmas Day (substitute - 25th is Saturday)
  '2027-12-28', // Boxing Day (substitute - 26th is Sunday)
];

const OrganizationSettings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [blockedDatesPage, setBlockedDatesPage] = useState(1);
  const BLOCKED_DATES_PER_PAGE = 5;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    vat_registration_number: '',
    company_registration_number: '',
    invoice_prefix: 'INV',
    default_vat_rate: '20.00',
    vat_registered: true,
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_sort_code: '',
    bank_iban: '',
    bank_swift_bic: '',
    invoice_terms_and_conditions: 'Payment is due within 30 days from the invoice date. Late payments may incur additional charges.',
    invoice_footer_text: '',
    invoice_notes: '',
    default_payment_terms_days: 30,
  });

  // Booking settings state
  const [bookingSettings, setBookingSettings] = useState({
    code: '',
    name: '',
    booking_enabled: true,
    booking_location_required: false,
    booking_block_weekends: true,
    booking_block_bank_holidays: true,
    booking_page_title: '',
    booking_page_message: '',
    blocked_dates: [],
  });

  useEffect(() => {
    fetchSettings();
    fetchBookingSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await invoicingService.getOrganizationSettings();
      setFormData({
        vat_registration_number: data.vat_registration_number || '',
        company_registration_number: data.company_registration_number || '',
        invoice_prefix: data.invoice_prefix || 'INV',
        default_vat_rate: data.default_vat_rate || '20.00',
        vat_registered: data.vat_registered !== undefined ? data.vat_registered : true,
        bank_name: data.bank_name || '',
        bank_account_name: data.bank_account_name || '',
        bank_account_number: data.bank_account_number || '',
        bank_sort_code: data.bank_sort_code || '',
        bank_iban: data.bank_iban || '',
        bank_swift_bic: data.bank_swift_bic || '',
        invoice_terms_and_conditions: data.invoice_terms_and_conditions || 'Payment is due within 30 days from the invoice date. Late payments may incur additional charges.',
        invoice_footer_text: data.invoice_footer_text || '',
        invoice_notes: data.invoice_notes || '',
        default_payment_terms_days: data.default_payment_terms_days || 30,
      });
    } catch (err) {
      setError('Failed to load organization settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingSettings = async () => {
    try {
      const { data } = await api.get('/v1/organizations/me/booking-settings/');
      setBookingSettings({
        code: data.code || '',
        name: data.name || '',
        booking_enabled: data.booking_enabled ?? true,
        booking_location_required: data.booking_location_required ?? false,
        booking_block_weekends: data.booking_block_weekends ?? true,
        booking_block_bank_holidays: data.booking_block_bank_holidays ?? true,
        booking_page_title: data.booking_page_title || '',
        booking_page_message: data.booking_page_message || '',
        blocked_dates: data.blocked_dates || [],
      });
    } catch (err) {
      console.error('Failed to load booking settings:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await invoicingService.updateOrganizationSettings(formData);
      setSuccess('Invoice settings updated successfully!');
      await fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update settings. Please ensure you have admin permissions.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBookingEnabled = async (e) => {
    const newValue = e.target.checked;
    try {
      await api.patch('/v1/organizations/me/booking-settings/update/', {
        booking_enabled: newValue,
      });
      setBookingSettings((prev) => ({
        ...prev,
        booking_enabled: newValue,
      }));
      setSuccess(newValue ? 'Booking form enabled' : 'Booking form disabled');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update setting.');
    }
  };

  const handleToggleBlockWeekends = async (e) => {
    const newValue = e.target.checked;
    try {
      await api.patch('/v1/organizations/me/booking-settings/update/', {
        booking_block_weekends: newValue,
      });
      setBookingSettings((prev) => ({
        ...prev,
        booking_block_weekends: newValue,
      }));
      setSuccess(newValue ? 'Weekends will be blocked' : 'Weekends are now selectable');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update setting.');
    }
  };

  const handleToggleBlockBankHolidays = async (e) => {
    const newValue = e.target.checked;
    try {
      await api.patch('/v1/organizations/me/booking-settings/update/', {
        booking_block_bank_holidays: newValue,
      });
      setBookingSettings((prev) => ({
        ...prev,
        booking_block_bank_holidays: newValue,
      }));
      setSuccess(newValue ? 'UK bank holidays will be blocked' : 'UK bank holidays are now selectable');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update setting.');
    }
  };

  const isUkBankHoliday = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return UK_BANK_HOLIDAYS.includes(dateStr);
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/book/${bookingSettings.code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDateClick = async (date) => {
    // Check if date is already blocked
    const existingBlock = bookingSettings.blocked_dates.find((bd) =>
      isSameDay(parseISO(bd.date), date)
    );

    if (existingBlock) {
      // Unblock the date
      try {
        await api.delete(`/v1/organizations/me/blocked-dates/${existingBlock.id}/`);
        setBookingSettings((prev) => ({
          ...prev,
          blocked_dates: prev.blocked_dates.filter((bd) => bd.id !== existingBlock.id),
        }));
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to remove blocked date.');
      }
    } else {
      // Block the date
      try {
        const { data } = await api.post('/v1/organizations/me/blocked-dates/', {
          date: format(date, 'yyyy-MM-dd'),
          reason: '',
        });
        setBookingSettings((prev) => ({
          ...prev,
          blocked_dates: [...prev.blocked_dates, data].sort((a, b) => new Date(a.date) - new Date(b.date)),
        }));
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to add blocked date.');
      }
    }
  };

  const handleDeleteBlockedDate = async (id) => {
    try {
      await api.delete(`/v1/organizations/me/blocked-dates/${id}/`);
      setBookingSettings((prev) => {
        const newDates = prev.blocked_dates.filter((bd) => bd.id !== id);
        // Reset page if current page is now empty
        const maxPage = Math.ceil(newDates.length / BLOCKED_DATES_PER_PAGE) || 1;
        if (blockedDatesPage > maxPage) {
          setBlockedDatesPage(maxPage);
        }
        return {
          ...prev,
          blocked_dates: newDates,
        };
      });
      setSuccess('Blocked date removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove blocked date.');
    }
  };

  const isDateBlocked = (date) => {
    return bookingSettings.blocked_dates.some((bd) => isSameDay(parseISO(bd.date), date));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const bookingFormUrl = `${window.location.origin}/book/${bookingSettings.code}`;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="Invoice Settings"
          />
          <Tab
            icon={<CalendarIcon />}
            iconPosition="start"
            label="Public Booking Form"
          />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Public Booking Form Settings Tab */}
          {activeTab === 1 && (
            <>

        {/* Booking Form Settings */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Booking Form
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={bookingSettings.booking_enabled}
                  onChange={handleToggleBookingEnabled}
                  name="booking_enabled"
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Enable Public Booking Form</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When disabled, customers cannot access the booking form
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Public Booking Form URL"
              value={bookingFormUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                      <IconButton onClick={copyUrl} edge="end">
                        <CopyIcon color={copied ? 'success' : 'inherit'} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              helperText="Share this URL with customers to book appointments"
            />
          </Grid>
        </Grid>

        {/* Blocked Dates Calendar */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Non-Bookable Days
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select dates when your business is closed (holidays, closures). These dates will be greyed out on the booking form.
          UK bank holidays are automatically greyed out.
        </Typography>

        {/* Block Weekends Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={bookingSettings.booking_block_weekends}
              onChange={handleToggleBlockWeekends}
              name="booking_block_weekends"
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Block Weekends</Typography>
              <Typography variant="caption" color="text.secondary">
                When enabled, Saturdays and Sundays cannot be booked
              </Typography>
            </Box>
          }
          sx={{ mb: 1 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={bookingSettings.booking_block_bank_holidays}
              onChange={handleToggleBlockBankHolidays}
              name="booking_block_bank_holidays"
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Block UK Bank Holidays</Typography>
              <Typography variant="caption" color="text.secondary">
                When enabled, UK bank holidays cannot be booked
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={null}
                onChange={(date) => handleDateClick(date)}
                minDate={new Date()}
                maxDate={addMonths(new Date(), 12)}
                shouldDisableDate={(date) => {
                  // Block weekends if setting is enabled
                  if (bookingSettings.booking_block_weekends && isWeekend(date)) return true;
                  // Block UK bank holidays if setting is enabled
                  if (bookingSettings.booking_block_bank_holidays && isUkBankHoliday(date)) return true;
                  return false;
                }}
                slots={{
                  day: (props) => {
                    const { day, ...other } = props;
                    const blocked = isDateBlocked(day);
                    const isBankHoliday = isUkBankHoliday(day);
                    const showBankHolidayStyle = isBankHoliday && bookingSettings.booking_block_bank_holidays;
                    return (
                      <PickersDay
                        {...other}
                        day={day}
                        selected={blocked}
                        sx={{
                          // Manually blocked dates - red
                          ...(blocked && {
                            backgroundColor: 'error.main !important',
                            color: 'white !important',
                            '&:hover': {
                              backgroundColor: 'error.dark !important',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'error.main !important',
                              color: 'white !important',
                            },
                          }),
                          // Bank holidays - orange (only when blocking is enabled)
                          ...(showBankHolidayStyle && !blocked && {
                            backgroundColor: '#ffb74d !important',
                            color: '#000 !important',
                            '&:hover': {
                              backgroundColor: '#ffa726 !important',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#ffb74d !important',
                              color: '#000 !important',
                              opacity: 1,
                            },
                          }),
                        }}
                      />
                    );
                  },
                }}
              />
            </LocalizationProvider>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Click a date to block/unblock it.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: '50%' }} />
                  <Typography variant="caption">Blocked</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: 'warning.light', borderRadius: '50%' }} />
                  <Typography variant="caption">Bank Holiday</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 320, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" gutterBottom>
                Blocked Dates ({bookingSettings.blocked_dates.length})
              </Typography>
              {bookingSettings.blocked_dates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No dates blocked. Select a date on the calendar to block it.
                </Typography>
              ) : (
                <>
                  <List dense sx={{ flex: 1, overflow: 'auto' }}>
                    {bookingSettings.blocked_dates
                      .slice(
                        (blockedDatesPage - 1) * BLOCKED_DATES_PER_PAGE,
                        blockedDatesPage * BLOCKED_DATES_PER_PAGE
                      )
                      .map((bd) => (
                        <ListItem key={bd.id} sx={{ bgcolor: 'grey.50', mb: 0.5, borderRadius: 1 }}>
                          <ListItemText
                            primary={format(parseISO(bd.date), 'EEEE, MMMM d, yyyy')}
                            secondary={bd.reason || 'No reason specified'}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteBlockedDate(bd.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                  {bookingSettings.blocked_dates.length > BLOCKED_DATES_PER_PAGE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                      <Pagination
                        count={Math.ceil(bookingSettings.blocked_dates.length / BLOCKED_DATES_PER_PAGE)}
                        page={blockedDatesPage}
                        onChange={(e, page) => setBlockedDatesPage(page)}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
            </>
          )}

          {/* Invoice Settings Tab */}
          {activeTab === 0 && (
            <form onSubmit={handleSubmit}>
          {/* Company Registration */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Company Registration
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="VAT Registration Number"
                name="vat_registration_number"
                value={formData.vat_registration_number}
                onChange={handleChange}
                placeholder="GB123456789"
                helperText="Your VAT registration number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Registration Number"
                name="company_registration_number"
                value={formData.company_registration_number}
                onChange={handleChange}
                placeholder="12345678"
                helperText="Companies House registration number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.vat_registered}
                    onChange={handleChange}
                    name="vat_registered"
                  />
                }
                label="VAT Registered"
              />
            </Grid>
          </Grid>

          {/* Invoice Settings */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Invoice Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Invoice Prefix"
                name="invoice_prefix"
                value={formData.invoice_prefix}
                onChange={handleChange}
                required
                helperText="Prefix for invoice numbers (e.g., INV)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Default VAT Rate (%)"
                name="default_vat_rate"
                value={formData.default_vat_rate}
                onChange={handleChange}
                required
                inputProps={{ step: '0.01', min: '0', max: '100' }}
                helperText="Standard VAT rate (e.g., 20.00 for 20%)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Default Payment Terms (days)"
                name="default_payment_terms_days"
                value={formData.default_payment_terms_days}
                onChange={handleChange}
                required
                inputProps={{ min: '1' }}
                helperText="Days until payment is due"
              />
            </Grid>
          </Grid>

          {/* Bank Details */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Bank Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Name"
                name="bank_account_name"
                value={formData.bank_account_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                name="bank_account_number"
                value={formData.bank_account_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sort Code"
                name="bank_sort_code"
                value={formData.bank_sort_code}
                onChange={handleChange}
                placeholder="12-34-56"
                helperText="UK sort code format"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="IBAN"
                name="bank_iban"
                value={formData.bank_iban}
                onChange={handleChange}
                helperText="International Bank Account Number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SWIFT/BIC"
                name="bank_swift_bic"
                value={formData.bank_swift_bic}
                onChange={handleChange}
                helperText="SWIFT/BIC code"
              />
            </Grid>
          </Grid>

          {/* Invoice Content */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Invoice Content
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Terms and Conditions"
                name="invoice_terms_and_conditions"
                value={formData.invoice_terms_and_conditions}
                onChange={handleChange}
                helperText="Default terms and conditions for invoices"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Footer Text"
                name="invoice_footer_text"
                value={formData.invoice_footer_text}
                onChange={handleChange}
                helperText="Optional footer text for invoices"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Default Notes"
                name="invoice_notes"
                value={formData.invoice_notes}
                onChange={handleChange}
                helperText="Default notes to include on invoices"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Invoice Settings'}
            </Button>
          </Box>
            </form>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default OrganizationSettings;
